import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useEffect, useState } from 'react';
import { cn } from '../../../../utils/styles';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Button } from '../Button';
import { Calendar } from '../Calendar';
import Label from '../Label';

interface IDatePickerWithRange extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'transparent';
  from?: Date;
  to?: Date;
  label?: string;
  handleOnChange?: (from?: Date, to?: Date) => void;
  dataFormat?: string;
  triggerClassName?: string;
  labelClassName?: string;
  iconClassName?: string;
}

function DatePickerWithRange({
  className,
  variant,
  from,
  triggerClassName,
  handleOnChange,
  iconClassName,
  labelClassName,
  dataFormat,
  label,
  to,
}: IDatePickerWithRange) {
  const [date, setDate] = useState<DateRange | undefined>({
    from,
    to,
  });

  useEffect(() => {
    if (handleOnChange) handleOnChange(date?.from, date?.to);
  }, [date]);

  return (
    <div className={cn('grid gap-[5px]', className)}>
      <Popover>
        {label ? <Label className={cn(labelClassName)}>{label}</Label> : null}
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              'p-[10px] justify-between text-left font-normal min-w-[300px]',
              !date && 'text-muted-foreground',
              {
                '!bg-transparent !text-white !border-white': variant === 'transparent',
              },
              triggerClassName,
            )}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, dataFormat || 'MM-dd-yy')} - {format(date.to, dataFormat || 'MM-dd-yy')}
                </>
              ) : (
                format(date.from, dataFormat || 'MM-dd-yy')
              )
            ) : (
              <span>Select date range</span>
            )}
            <CalendarIcon className={cn('ml-[5px] h-[18px] w-[18px] opacity-70', iconClassName)} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={setDate}
            numberOfMonths={2}
            withYearPicker
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

DatePickerWithRange.defaultProps = {
  variant: '',
  handleOnChange: () => undefined,
  from: undefined,
  to: undefined,
  dataFormat: undefined,
  triggerClassName: '',
  iconClassName: '',
  labelClassName: '',
  label: undefined,
};

export default DatePickerWithRange;
